<script setup lang="ts">
import { onMounted, useEventListener, useTemplateRef, useElementSize, whenever, definePageMeta, usePreviewBridge, useHeroConfig } from '#imports'
import type { HeroContent } from '@nimbus/types'
// Namespace import, not `{ z }`: Vite drops the re-exported `z` binding, so the
// named form resolves to undefined under the Nuxt test environment.
import * as z from 'zod/v4'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true,
})

// Hero typography is vw/clamp based and breakpoint driven, so the layout has to
// happen at a real desktop width; the embedder scales the frame down to fit its
// panel rather than letting the hero render its mobile variant.
const PREVIEW_WIDTH = '1280px'
const INITIAL_HEIGHT = '600px'

/**
 * Mirrored by marketing-backoffice's `composables/heroes/heroPreviewPayload.ts`
 * — the keys below are the contract between the two, so update both together.
 * Keys map 1:1 onto hero field slugs; if a mapping ever renames one, note it on
 * both sides (a loose passthrough key cannot fail loudly on a rename).
 */
const DataSchema: z.ZodType<HeroContent> = z.looseObject({
    // Required by HeroContent — defaulted so an untouched form still renders.
    design: z.literal('game').default('game'),
    title: z.string().default(''),
    button_text: z.string().default(''),
    button_text_color: z.string().default(''),
    button_url: z.string().default(''),
    foreground_image: z.string().default(''),
    background_image: z.string().default(''),
    show_terms_below_text: z.boolean().default(false),
    game_slug: z.string().default(''),
    // Optional, but rendered by the hero or its terms band.
    tagline: z.string().optional(),
    subtitle: z.string().optional(),
    button_color: z.string().optional(),
    splash_image: z.string().optional(),
    usp_1: z.string().optional(),
    usp_2: z.string().optional(),
    gradient_inner_color: z.string().optional(),
    gradient_outer_color: z.string().optional(),
    gradient_inner_opacity: z.coerce.number().optional(),
    gradient_outer_opacity: z.coerce.number().optional(),
    short_terms: z.string().optional(),
    short_terms_2: z.string().optional(),
    long_terms: z.string().optional(),
    long_terms_2: z.string().optional(),
    label: z.string().optional(),
    label_2: z.string().optional(),
    enable_video: z.union([z.boolean(), z.string()]).optional(),
    video_src: z.string().optional(),
    video_id: z.string().optional(),
    video_poster_src: z.string().optional(),
    custom_symbol: z.string().optional(),
})

const { data, send } = usePreviewBridge(DataSchema)

const { imagePreset } = useHeroConfig()

const content = useTemplateRef<HTMLDivElement>('content')
const { height } = useElementSize(content)

function sendResize(height: string) {
    send({ type: 'resize', payload: { height, width: PREVIEW_WIDTH } })
}

whenever(height, h => sendResize(`${h}px`))

// Claim the desktop width before there is anything to measure: breakpoints and
// vw units resolve against the iframe viewport, so it must not spend the first
// paint at PreviewFrame's 414px default.
onMounted(() => sendResize(INITIAL_HEIGHT))

// The CTA navigates and tracks `hero_cta_button`, the game tile opens the game.
// Swallow clicks before they reach either.
if (import.meta.client) {
    useEventListener(window, 'click', (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }, { capture: true })
}
</script>

<template>
    <UIApp>
        <div v-if="!data">
            <UILoader bg>
                Waiting for data
            </UILoader>
        </div>

        <template v-else>
            <div v-if="data.error">
                <pre>{{ data.error.issues }}</pre>
            </div>

            <div v-else ref="content" class="preview-hero">
                <NimbusHeroContainer :data="data.data" :preset="imagePreset" />
                <StickyTerms :hero-data="data.data" reset-margin />
            </div>
        </template>
    </UIApp>
</template>

<style scoped lang="scss">
// The frame is the hero, so the site's page gutters would only be grey bars.
.preview-hero :deep(.hero__container) {
    padding-left: 0;
    padding-right: 0;
}
</style>
